import { useState, useEffect, useCallback, useRef } from 'react';
import { addViews } from '../Api';
import {useFields} from "./useFields";
import {useAuth0} from "@auth0/auth0-react";
import { isBagelAiTab } from "../utils/viewUtils";

/**
 * Custom hook for syncing selected view with URL state and handling view initialization
 * Also persists to page-specific localStorage
 * 
 * @param views - Array of views for the current page
 * @param selectedViewName - Current selected view name from URL (from useIdeasUrlState)
 * @param setSelectedViewName - Function to update selected view name in URL
 * @param subPageKey - Current subpage key
 * @param subPage - Current subpage data
 * @param subPageLoading - Whether subpage is loading
 * @param setSavedViews - Function to update saved views list
 * @returns {selectedView, setSelectedView}
 */
export const useIdeasPageViewSync = (
  views: any[] | undefined,
  selectedViewName: string | null,
  setSelectedViewName: (name: string | null) => void,
  subPageKey: string | null,
  subPage: any,
  subPageLoading: boolean,
  setSavedViews: (views: any[]) => void
) => {
  const [selectedView, setSelectedViewState] = useState<any>(null);
  const selectedViewIdRef = useRef<string | null>(null);
  const selectedViewNameRef = useRef<string | null>(null);

  const { fields } = useFields();
  const auth0 = useAuth0();

  // Wrapped setter that updates both local state, URL, and localStorage
  const setSelectedView = useCallback((view: any) => {
    setSelectedViewState(view);
    selectedViewIdRef.current = view?._id || null;
    selectedViewNameRef.current = view?.name || null;
    setSelectedViewName(view?.name || null);
    if (view && view._id) {
      if (subPageKey) {
        localStorage.setItem(`ideas-view-${subPageKey}`, view._id);
      } else {
        localStorage.setItem('ideas-view-all', view._id);
      }
    }
  }, [setSelectedViewName, subPageKey]);


  // Merges a default filter into a filters array.
  // If a filter with the same name already exists (non-default), unions the selected arrays.
  // Otherwise prepends it marked as default: true so it can be cleaned up on subpage switch.
  const mergeDefaultFilterIntoViewFilters = (filters: any[], defaultFilter: any) => {
    const key = (defaultFilter?.name || defaultFilter?.parent || '').toLowerCase();
    const existingIdx = filters.findIndex(
      (f: any) => !f.default && (f?.name || f?.parent || '').toLowerCase() === key
    );
    if (existingIdx >= 0) {
      // View already configures this field — its own selection wins, no union.
      return;
    }
    filters.unshift({ ...defaultFilter, default: true });
  };

  const initializeViews = () => {
    // Wait for views to be loaded AND subpage data to be ready (if we have a subPageKey)
    if (views && views.length > 0 && (!subPageKey || !subPageLoading)) {

      let filteredViews;
      const enableBagelAI = subPage?.enableBagelAI;

      if(subPageKey){
        //update views if subpageKey not exist in view.subPageKeys
        const processedViews = views.map( (view: any) => {
          if (!view.subPageKey) {
            view.subPageKey = [];
          }
          if (view.isSubPageView && !view?.subPageKeys?.includes(subPageKey)) {
            view?.subPageKeys?.push(subPageKey);
            try {
              addViews({
                ...view,
                subPageKey: subPageKey,
              }, auth0);
            } catch (error) {
              console.error('Failed to update view subPageKey:', error);
            }
          }
          return view;
        })
        filteredViews = processedViews.filter((view: any) =>
          (enableBagelAI && isBagelAiTab(view)) ||
          view.subPageKeys?.includes(subPageKey)
        );
        //add  subpage's defaultFilter  in each views's filters array, merging selected values if same filter exists
        filteredViews.forEach((view: any) => {
          if(!view.filters){
            view.filters = [];
          }

          // Remove existing default filters from previous subpages first
          view.filters = view.filters.filter((f: any) => !f.default);
          // Add current subpage's default filters, merging with existing view filter if same name
          const defaults = Array.isArray(subPage?.defaultFilters)
            ? subPage.defaultFilters
            : subPage?.defaultFilters ? [subPage.defaultFilters] : [];
          defaults.forEach((defaultFilter: any) => {
            mergeDefaultFilterIntoViewFilters(view.filters, defaultFilter);
          });
        });
      } else {
        filteredViews = views.filter((view: any) =>
          (view?.createdBy !== 'subpage') || (view?.createdBy == 'subpage' && view?.isSubPageView)
        );
        //add defaultFilter  in each views's filters array, merging selected values if same filter exists
        filteredViews.forEach((view: any) => {
          if (!view.filters) { view.filters = []; }
          // Remove existing default filters first (for consistency)
          view.filters = view.filters.filter((f: any) => !f.default);
          // Add main page default filters, merging with existing view filter if same name
          const defaults = Array.isArray(fields?.defaultFilters)
            ? fields.defaultFilters
            : fields?.defaultFilters ? [fields.defaultFilters] : [];
          defaults.forEach((defaultFilter: any) => {
            mergeDefaultFilterIntoViewFilters(view.filters, defaultFilter);
          });
        });
      }

      setSavedViews(filteredViews);

      // Try to load saved view from localStorage for this specific page
      let viewToSelect = null;
      let savedViewId = null;
      if (subPageKey) {
        savedViewId = localStorage.getItem(`ideas-view-${subPageKey}`);
      } else {
        savedViewId = localStorage.getItem('ideas-view-all');
      }

      if (selectedViewName) {
        const urlView = filteredViews.find((sv:any) => sv.name.toLowerCase() === selectedViewName.toLowerCase());
        if (urlView) {
          viewToSelect = urlView;
        }
        // "All" is the default page label for /ideas/all - not a saved view. Use a synthetic view so URL stays All and query runs.
        if (!viewToSelect && selectedViewName.toLowerCase() === "all") {
          viewToSelect = { name: "All", filters: [], _id: null, isGlobal: false, isSubPageView: false };
        }
      }

      // If no view found from URL, try localStorage (but not when URL explicitly has selectedView=All)
      if (!viewToSelect && savedViewId && selectedViewName?.toLowerCase() !== "all") {
        // Check if saved view exists in current page's filtered views
        const savedView = filteredViews.find((view: any) => view._id === savedViewId);
        if (savedView) {
          viewToSelect = savedView;
        }
      }

      // If still no view selected, fall back to first view (unless URL explicitly has selectedView=All)
      if (!viewToSelect && filteredViews.length > 0 && selectedViewName?.toLowerCase() !== "all") {
        viewToSelect = filteredViews[0];
      }

      setSelectedView(viewToSelect);
    }
  };

  // Handle view initialization when views, subpage, or URL view name changes
  useEffect(() => {
    initializeViews();
  }, [views, subPageKey, subPage, subPageLoading, selectedViewName, fields, auth0]);

  return { selectedView, setSelectedView, setSelectedViewState };
};

